import { Component, ElementRef, HostListener, OnInit, OnDestroy, SimpleChanges, ViewChild } from "@angular/core";
import { AuthService } from "../auth/auth.service";
import { AccountAccessInfo, SignupUserInfo, User } from "../workbenchDashboard/workbench.model";
import { Location } from "@angular/common";
import { DocumentationComponent } from "../analysis/documentation/documentation.component";
import { fromEvent, Observable, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { NavigationEnd, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { LOCAL_STORAGE_KEYS } from "../shared/constants/storage-keys";
import { LINKS } from "../shared/constants/links";

@Component({
  selector: "tdms-header",
  templateUrl: "./app-header.component.html",
  styleUrls: ["./app-header.component.scss"],
  standalone: false,
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  readonly LINKS = LINKS;
  tdmUser: User | null = null;
  isUser = false;
  accountAccessInfo: AccountAccessInfo | null = null;
  displayTopNavVizLink = false;
  displayTopNavWorkbenchLink = false;

  isToggle = false;
  isOpen = false;
  isAnalysis = false;
  isWorkbench = false;
  isHelp = false;
  isDropdownMenu = false;
  isRequestAccess = false;
  resize!: Observable<Window>;
  resizeSubscription!: Subscription;
  errMsg = "";
  displayOnboard = false;

  isContactDetailsVisible = false;

  @ViewChild("contactDetails", { static: false }) contactDetails!: ElementRef;

  @HostListener("keydown.escape")
  hideModal(): void {
    this.isRequestAccess = false;
  }

  constructor(
    public auth: AuthService,
    private location: Location,
    private dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
  ) {
    router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.setDashboard();
      }
    });
  }

  ngOnInit() {
    this.auth.loginActivity$.subscribe((activity) => {
      this.setUser();
    });

    if (this.authService.getCurrentlyLoggedInUser() !== null) {
      this.setUser();
    } else {
      this.isUser = this.checkIsUser();
    }

    //Call the resize function whenever a resize event occurs.
    this.resize = fromEvent<Window>(window, "resize").pipe(debounceTime(100));

    // When window resizes close menu
    this.resizeSubscription = this.resize.subscribe(() => {
      this.isDropdownMenu = false;
    });

    //isHelpcheck...
    if (this.location.path().includes("/analysis")) this.isHelp = true;

    //Which Dashboard is selected
    //this.setDashboard();
    this.router.events.subscribe(() => {
      const currentUrl = this.router.url.substring(1);
      const displayWecomePage = localStorage.getItem(LOCAL_STORAGE_KEYS.DISPLAY_WELCOME_PAGE);
      if (currentUrl === "workbenchdashboard" && (displayWecomePage === "true" || !displayWecomePage)) {
        this.displayOnboard = true;
        localStorage.setItem(LOCAL_STORAGE_KEYS.DISPLAY_WELCOME_PAGE, "false");
      }
    });
  }

  ngOnchanges(changes: SimpleChanges) {}

  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }

  goToHomePage() {
    this.router.navigate(["home"]);
  }

  showDashboardLinks() {
    return (
      this.tdmUser &&
      Object.keys(this.tdmUser).length > 0 &&
      !this.location.path().includes("/welcome")
    );
  }

  goToWorkbench() {
    const user: User = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER) ?? "{}");

    if (this.authService.isLimitedSubscription()) {
      this.isRequestAccess = true;
    } else {
      this.router.navigate(["/workbenchdashboard"]);
    }
    this.isDropdownMenu = false;
  }

  goToAnalysis() {
    this.displayOnboard = false;
    const route = "/analysis/dashboard";
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_DASHBOARD, route);
    this.router.navigate([route]);
    this.isDropdownMenu = false;
  }

  setUser() {
    this.tdmUser = this.authService.getCurrentlyLoggedInUser();
    this.isUser = this.checkIsUser();

    this.displayTopNavWorkbenchLink = this.navBarWorkbenchLinkCheck() ?? false;

    this.displayTopNavVizLink =
      (this.tdmUser !== null && !!this.tdmUser.hasMethodsAccessResp && this.tdmUser.hasMethodsAccessResp.hasMethodsAccess) ?? false;
  }

  checkIsUser(): boolean {
    return (!!this.tdmUser && this.authService.hasAccessToWorkBench()) || this.authService.hasAccessToVisualization();
  }

  navBarWorkbenchLinkCheck(): boolean {
    return !!(
      this.tdmUser !== null &&
      this.tdmUser.hasWorkbenchAccessResp &&
      (this.tdmUser.hasWorkbenchAccessResp.create ||
        this.tdmUser.hasWorkbenchAccessResp.read ||
        this.tdmUser.hasWorkbenchAccessResp.accountType === "limited")
    );
  }

  setDashboard() {
    if (this.location.path().includes("/analysis")) {
      this.isAnalysis = true;
      this.isWorkbench = false;
      this.isHelp = true;
    } else if (
      this.location.path().includes("/home") ||
      this.location.path().includes("/unauthorized") ||
      this.location.path().includes("/welcome")
    ) {
      this.isAnalysis = false;
      this.isWorkbench = false;
    } else {
      this.isHelp = false;
      this.isAnalysis = false;
      this.isWorkbench = true;
    }
  }

  openDocumentation() {
    this.dialog.open(DocumentationComponent, {
      disableClose: true,
      width: "90vw",
      height: "90vh",
      // maxWidth: "none",
      data: { tab: this.getStartingTab() },
    });
  }

  getStartingTab(): string {
    if (
      this.location.path().includes("/analysis/search") ||
      this.location.path().includes("/analysis/refinecontent") ||
      this.location.path().includes("/analysis/createdataset")
    ) {
      return "Create a Project";
    } else if (this.location.path().includes("/analysis/viz/geoanalysis")) {
      return "Geographic Analysis";
    } else if (this.location.path().includes("/analysis/viz/tm")) {
      return "Topic Modeling";
    } else if (this.location.path().includes("/analysis/viz/sa")) {
      return "Sentiment Analysis";
    }
    return "Visualization Dashboard";
  }

  openHelpWindow() {
    if (this.isAnalysis) {
      this.openDocumentation();
    } else {
      this.displayOnboard = true;
    }
    this.isDropdownMenu = false;
  }

  onCloseOnBoardEvent(isClose: boolean) {
    if (isClose) {
      this.displayOnboard = false;
    }
  }

  toggleContactDetails() {
    this.isContactDetailsVisible = !this.isContactDetailsVisible;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    if (!this.contactDetails.nativeElement.contains(event.target)) {
      this.isContactDetailsVisible = false;
    }
  }
}
